import React from 'react';
import { LoadingIndicator } from 'library';
import { AutoComplete, Caption, Row, Stack, Text } from 'library/ui';
import { ProviderSelection } from 'parent-portal/forms/JobRequestTypes';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import type { ExtendedProviderSelection } from './utilities';

interface Props {
    selected: ProviderSelection[];
    onChange: (selectedProviderUserIds: ProviderSelection[]) => void;
    multiple?: boolean;
    clearOtherInput?: () => void;
}

function statusTone(status?: string): 'error' | 'warning' | 'secondary' {
    const normalized = status?.toLowerCase();
    if (normalized === 'unavailable') return 'error';
    if (normalized === 'out_of_compliance') return 'warning';
    return 'secondary';
}

export default function PastWorkersInput({ selected, onChange, multiple = true, clearOtherInput }: Props) {
    const { pastWorkers, loadingPastWorkers } = useJobFormContext();

    const pastWorkerOptions: ExtendedProviderSelection[] = pastWorkers.map((worker) => ({
        id: worker.id,
        name: `${worker.first_name} ${worker.last_name}`,
        status: worker.status,
        sanitizedStatus: worker.status ? `(${worker.status.replaceAll('_', ' ').capitalize()})` : '',
    }));

    const onDropdownSelect = (value: ExtendedProviderSelection | ExtendedProviderSelection[] | null) => {
        if (!multiple && !!value) clearOtherInput?.();

        if (!value) {
            onChange([]);
        } else if (Array.isArray(value)) {
            const values = value.map((it) => it.id);
            onChange(pastWorkerOptions.filter((it) => values.includes(it.id)));
        } else {
            const choice = pastWorkerOptions.find((it) => it.id === value.id);
            onChange(choice ? [choice] : []);
        }
    };

    const renderContent = () => {
        if (loadingPastWorkers) {
            return <LoadingIndicator />;
        }
        if (pastWorkers.length === 0) {
            return <Text>It looks like you have not set any preferred workers.</Text>;
        }
        return (
            <Stack gap="sm">
                <AutoComplete<ExtendedProviderSelection, boolean>
                    multiple={multiple}
                    options={pastWorkerOptions}
                    selected={
                        multiple
                            ? pastWorkerOptions.filter((it) => selected.some((s) => s.id === it.id))
                            : selected[0] ?? null
                    }
                    onSelect={onDropdownSelect}
                    getOptionLabel={(option) => `${option.name}`}
                    getOptionSelected={(option, value) => option.id === value.id}
                    getOptionDisabled={(option) => option.status === 'out_of_compliance'}
                    renderOption={(option) => (
                        <Row gap="sm" align="center">
                            <Text>{option.name}</Text>
                            {option.sanitizedStatus ? (
                                <Text size="xs" weight="bold" tone={statusTone(option.status)}>
                                    {option.sanitizedStatus}
                                </Text>
                            ) : null}
                        </Row>
                    )}
                    placeholder="Click here to search for past workers"
                />
                <Caption>Select workers to recommend for this job.</Caption>
            </Stack>
        );
    };

    return (
        <Stack gap="sm">
            <Text weight="bold">Past Workers</Text>
            {renderContent()}
        </Stack>
    );
}
